import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import {
  loadUnicodeFontFamily,
  detectLanguage,
  requiresUnicodeFont,
} from './pdfFonts';
import type { PDFFont } from './pdfFonts';
import {
  normalizeContent,
  sanitizeForPdf,
  wrapText,
  safeDrawText,
  extractAndPreserveReferences,
  convertReferencesToPlaintext,
} from './pdfTextUtils';

/**
 * Translation overrides for creating translated PDF documents.
 * When provided, these values replace the default English text.
 */
export interface PdfTranslationOverrides {
  /** Translated document title */
  title?: string;
  /** Translated date string (e.g., "15 de enero de 2024") */
  date?: string;
  /** Translated "Prepared by" line (e.g., "Preparado por: Fernando Hidalgo") */
  preparedBy?: string;
  /** Firm name (typically kept as-is) */
  firmName?: string;
  /** Translated full copyright notice */
  copyrightNotice?: string;
  /** Translated short copyright for footers */
  copyrightShort?: string;
  /** Translated page number format (e.g., "Página {current}") */
  pageNumberFormat?: string;
}

interface BuildResearchPdfParams {
  title: string;
  prompt: string;
  content: string;
  createdAt?: Date;
  /** Optional translation overrides for creating translated PDFs */
  translations?: PdfTranslationOverrides;
}

// Firm information for cover page and footer.
//
// MT-QA-BUG-001: this literal is the LAST-RESORT fallback only — callers
// that know their tenant (Deep Research, Maestro, LDC) must thread the
// active tenant's firm name via `translations.firmName`. The Fernando
// widget path is the only code path that should rely on this fallback.
const FIRM_NAME = 'The Law Offices of Fernando Hidalgo, Inc.';

// MT-QA-BUG-001 follow-up (2026-05-08 §132a regression): PDFs were
// shipping with the placeholder string "Legal AI Assistant" on the title
// page. Replace with the product brand label so every tenant's PDF reads
// "Generated by: Prest0n". The constant is intentionally NOT tenant-
// specific — it's the product label, not a firm name.
const PRODUCT_NAME = 'Prest0n';

// A4 page dimensions in points
const A4_WIDTH = 595.3;
const A4_HEIGHT = 841.9;

/**
 * Build a professional PDF report with raw research content.
 */
export async function buildResearchPdf(params: BuildResearchPdfParams): Promise<File> {
  const createdAt = params.createdAt || new Date();
  const year = createdAt.getFullYear();
  const translations = params.translations || {};

  // Use translated values if provided, otherwise use defaults
  const firmName = translations.firmName || FIRM_NAME;
  const dateStr = translations.date || createdAt.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const generatedByText = translations.preparedBy || `Generated by: ${PRODUCT_NAME}`;
  const facilitatedByText = `Facilitated by: ${firmName}`;
  const copyrightNotice = translations.copyrightNotice || `(c) ${year} ${firmName}. Generated by ${PRODUCT_NAME}. Facilitated by ${firmName}. All rights reserved.`;
  const copyrightShort = translations.copyrightShort || `© ${year} ${firmName}`;
  const pageNumberFormat = translations.pageNumberFormat || 'Page {current}';

  const pdfDoc = await PDFDocument.create();

  // Detect if content requires Unicode fonts (for Korean/CJK characters)
  const contentToCheck = [
    params.content,
    params.title,
    params.prompt,
    translations.title,
    translations.date,
    translations.preparedBy,
    translations.copyrightNotice,
    translations.copyrightShort,
    translations.pageNumberFormat,
  ].filter(Boolean).join(' ');

  const needsUnicode = requiresUnicodeFont(contentToCheck);
  const languageHint = detectLanguage(contentToCheck);

  // Load appropriate fonts based on content language
  let font: PDFFont;
  let boldFont: PDFFont;
  let italicFont: PDFFont;

  if (needsUnicode) {
    // Load Unicode fonts (Noto Sans KR) for CJK content
    const fonts = await loadUnicodeFontFamily(pdfDoc, languageHint);
    font = fonts.regular;
    boldFont = fonts.bold;
    italicFont = fonts.italic; // CJK fonts typically don't have italic, using regular
  } else {
    // Use Times-Roman (serif) as specified in prompt for Latin-only content
    font = await pdfDoc.embedFont(StandardFonts.TimesRoman);
    boldFont = await pdfDoc.embedFont(StandardFonts.TimesRomanBold);
    italicFont = await pdfDoc.embedFont(StandardFonts.TimesRomanItalic);
  }

  const titleSan = sanitizeForPdf(params.title, needsUnicode);
  const promptSan = sanitizeForPdf(params.prompt, needsUnicode);

  // Phase 1: Split content into body and references to preserve URLs in references section
  const { body, references } = extractAndPreserveReferences(params.content);

  // Phase 1a: Normalize body content (strips markdown formatting including links - correct for body)
  const normalizedBody = normalizeContent(body, needsUnicode);

  // Phase 1b: Normalize references separately, preserving URL info
  // Convert [label](url) to "label (url)" before stripping other markdown formatting
  let normalizedReferences = '';
  if (references) {
    const refsWithVisibleUrls = convertReferencesToPlaintext(references);
    normalizedReferences = normalizeContent(refsWithVisibleUrls, needsUnicode);
  }

  const normalizedContent = normalizedBody + (normalizedReferences ? '\n\n' + normalizedReferences : '');

  // Set PDF metadata (use translated title if provided)
  const pdfTitle = translations.title || titleSan || 'Legal Research Report';
  pdfDoc.setTitle(pdfTitle);
  pdfDoc.setAuthor(firmName);
  pdfDoc.setSubject(promptSan || 'Legal Research Report');
  // PDF metadata keywords — derived from the active tenant so Fernando
  // branding does not leak into T1/T2 output (MT-QA-BUG-001).
  pdfDoc.setKeywords(['legal research', 'law offices', firmName]);
  pdfDoc.setProducer(PRODUCT_NAME);
  pdfDoc.setCreator(PRODUCT_NAME);
  pdfDoc.setCreationDate(createdAt);
  pdfDoc.setModificationDate(createdAt);

  // Layout constants per formatting specification
  const margin = 54;         // Left/right margin (0.75 inches)
  const topMargin = 39;      // Top margin from top edge of page
  const footerMargin = 36;   // Reserved bottom area for footer block
  const lineHeight = 15;     // Body text leading (11pt * ~1.36)
  const allPages: ReturnType<typeof pdfDoc.addPage>[] = [];

  // Track current page and cursor
  let page = pdfDoc.addPage([A4_WIDTH, A4_HEIGHT]);
  allPages.push(page);
  let { width, height } = page.getSize();
  let maxWidth = width - margin * 2;
  let cursorY = height - topMargin;
  let lastContentPageIndex = 0; // Track last page that received content

  const addNewPage = () => {
    page = pdfDoc.addPage([A4_WIDTH, A4_HEIGHT]);
    allPages.push(page);
    ({ width, height } = page.getSize());
    maxWidth = width - margin * 2;
    cursorY = height - topMargin;
  };

  const ensureSpace = (needed: number) => {
    if (cursorY - needed < footerMargin + 14) {
      addNewPage();
    }
  };

  // Draw wrapped text with configurable leading for different heading levels
  const drawWrapped = (text: string, size: number, usedFont: typeof font, indent: number, extraGap = 8, color = rgb(0, 0, 0), leading?: number) => {
    const lh = leading || lineHeight;
    const wrapped = wrapText(text, usedFont, size, maxWidth - indent);
    wrapped.forEach(line => {
      ensureSpace(lh);
      safeDrawText(page, line, { x: margin + indent, y: cursorY, size, font: usedFont, color });
      cursorY -= lh;
      lastContentPageIndex = allPages.length - 1; // Track that this page has content
    });
    cursorY -= extraGap;
  };

  // ============================================================================
  // COVER PAGE
  // ============================================================================
  // Title begins approximately 72pt from the top of the page
  cursorY = height - 72;

  // Title (uses translation if provided) — Times-Bold 22pt, centered
  const displayTitle = translations.title || titleSan || 'Legal Research Report';
  const titleLines = wrapText(displayTitle, boldFont, 22, maxWidth);
  titleLines.forEach(line => {
    const titleWidth = boldFont.widthOfTextAtSize(line, 22);
    safeDrawText(page, line, { x: (width - titleWidth) / 2, y: cursorY, size: 22, font: boldFont, color: rgb(0, 0, 0) });
    cursorY -= 28;
  });

  // ~36pt vertical spacer below the title block
  cursorY -= 36;

  // Attribution lines (each centered, stacked vertically)
  // Generated by (uses translation if provided) — Times-Roman 12pt
  const generatedByWidth = font.widthOfTextAtSize(generatedByText, 12);
  safeDrawText(page, generatedByText, { x: (width - generatedByWidth) / 2, y: cursorY, size: 12, font, color: rgb(0, 0, 0) });
  cursorY -= 18;

  // Facilitated by line removed (Issue request to remove firm from cover page below date)
  // safeDrawText(page, facilitatedByText, { x: (width - facilitatedByWidth) / 2, y: cursorY, size: 12, font, color: rgb(0, 0, 0) });
  // cursorY -= 18;

  // Date — Times-Roman 11pt, dark gray (#4D544D)
  const dateWidth = font.widthOfTextAtSize(dateStr, 11);
  safeDrawText(page, dateStr, { x: (width - dateWidth) / 2, y: cursorY, size: 11, font, color: rgb(0.302, 0.329, 0.302) });

  // ~24pt vertical spacer below the attribution block
  cursorY -= 24;

  // Copyright notice on cover page — removed the central notice so it only appears in footer.
  cursorY -= 12;
  lastContentPageIndex = 0; // Cover page has content

  // ============================================================================
  // CONTENT PAGES — structured heading hierarchy
  // ============================================================================
  addNewPage();

  // Document title block at top of page 2 — ALL CAPS, Times-Bold 14pt
  const docTitleText = displayTitle.toUpperCase();
  const docTitleLines = wrapText(docTitleText, boldFont, 14, maxWidth);
  docTitleLines.forEach(line => {
    ensureSpace(18);
    safeDrawText(page, line, { x: margin, y: cursorY, size: 14, font: boldFont, color: rgb(0, 0, 0) });
    cursorY -= 18; // Leading for 14pt
    lastContentPageIndex = allPages.length - 1;
  });
  cursorY -= 13; // Space after document title block

  // Render paragraphs with heading hierarchy detection
  let inReferences = false;

  // Helper: detect heading level from a line of text.
  // Returns { level, text } if the line is a heading, or null for body text.
  // Handles markdown headings (## Heading), Roman numeral sections (II. Heading),
  // and combined formats (## II. Heading) that Perplexity deep research may produce.
  const detectHeading = (line: string): { level: number; text: string } | null => {
    // 1. Markdown headings: # through ######
    const mdMatch = line.match(/^(#{1,6})\s+(.+)/);
    if (mdMatch) {
      return { level: mdMatch[1].length, text: mdMatch[2] };
    }
    // 2. Roman numeral section headers (I. through XX.) — treat as H2
    const romanMatch = line.match(/^([IVX]{1,5})\.\s+(.+)/);
    if (romanMatch) {
      return { level: 2, text: `${romanMatch[1]}. ${romanMatch[2]}` };
    }
    return null;
  };

  // Helper: check if text is a references/bibliography section name
  const isReferencesHeader = (text: string): boolean =>
    /^(?:[IVX]{1,5}\.\s+)?(references|bibliography|sources?\s*cited|works\s*cited|complete\s*source\s*citations)$/i.test(text);

  const paragraphs = normalizedContent.split('\n');
  for (const para of paragraphs) {
    const trimmed = para.trim();
    if (!trimmed) {
      cursorY -= 6;
      continue;
    }

    const heading = detectHeading(trimmed);

    if (heading) {
      // Check if this is the References section header
      if (isReferencesHeader(heading.text)) {
        inReferences = true;
      }

      if (heading.level <= 2) {
        // H2 — Section header: Times-Bold 13pt, x=54pt (margin), leading=17
        cursorY -= 10; // spaceBefore
        drawWrapped(heading.text, 13, boldFont, 0, 9, rgb(0, 0, 0), 17);
      } else if (heading.level === 3) {
        // H3 — Sub-section header: Times-Bold 12pt, x=62pt (margin+8), leading=16
        cursorY -= 8; // spaceBefore
        drawWrapped(heading.text, 12, boldFont, 8, 7, rgb(0, 0, 0), 16);
      } else {
        // H4+ — Sub-sub-section header: Times-Bold 11pt, x=62pt (margin+8), leading=15
        cursorY -= 8; // spaceBefore
        drawWrapped(heading.text, 11, boldFont, 8, 6, rgb(0, 0, 0), 15);
      }
    } else {
      // Check for unheaded references section marker (plain "References" line)
      if (isReferencesHeader(trimmed)) {
        inReferences = true;
        cursorY -= 10;
        drawWrapped(trimmed, 13, boldFont, 0, 9, rgb(0, 0, 0), 17);
        continue;
      }

      // Body text — Times-Roman 11pt, x=54pt (margin)
      // Strip remaining structural markers (bullets, numbers)
      const text = trimmed
        .replace(/^[-*]\s+/, '')
        .replace(/^\d+\.\s+/, '');
      if (text) {
        // Reference entries use 6pt spacing; body text uses 8pt
        const gap = inReferences ? 6 : 8;
        drawWrapped(text, 11, font, 0, gap);
      }
    }
  }

  // ============================================================================
  // REMOVE TRAILING EMPTY PAGES
  // ============================================================================
  // Pages created by ensureSpace() but never written to should be removed
  while (allPages.length - 1 > lastContentPageIndex && allPages.length > 1) {
    pdfDoc.removePage(allPages.length - 1);
    allPages.pop();
  }

  // ============================================================================
  // ADD PAGE NUMBERS AND FOOTERS
  // ============================================================================
  const totalPages = allPages.length;
  allPages.forEach((pg, idx) => {
    const pgSize = pg.getSize();
    const pageNum = idx + 1;

    // Page number (bottom center) — Times-Roman 9pt, medium gray (#666666)
    const pageNumText = pageNumberFormat
      .replace('{current}', String(pageNum))
      .replace('{total}', String(totalPages));
    const pageNumWidth = font.widthOfTextAtSize(pageNumText, 9);
    safeDrawText(pg, pageNumText, {
      x: (pgSize.width - pageNumWidth) / 2,
      y: 35,
      size: 9,
      font,
      color: rgb(0.4, 0.4, 0.4),
    });

    // Footer copyright (on every page) — Times-Italic 8pt, gray (#808080)
    // if (idx > 0) {
      const footerWidth = italicFont.widthOfTextAtSize(copyrightShort, 8);
      safeDrawText(pg, copyrightShort, {
        x: (pgSize.width - footerWidth) / 2,
        y: 22,
        size: 8,
        font: italicFont,
        color: rgb(0.502, 0.502, 0.502),
      });
    // }
  });

  const pdfBytes = await pdfDoc.save();
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });

  // Generate a human-friendly filename from the subject
  const slugify = (text: string): string => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 50);
  };
  const subjectSlug = slugify(params.prompt || params.title || 'report');
  const dateSlug = createdAt.toISOString().slice(0, 10);
  const filename = `${subjectSlug}-${dateSlug}.pdf`;

  return new File([blob], filename, { type: 'application/pdf', lastModified: createdAt.getTime() });
}
